import React, { ImgHTMLAttributes, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ImageWithSkeletonProps extends ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
  imgClassName?: string;
  startVisible?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  eagerLoad?: boolean;
}

const normalizeSrc = (raw?: string) => raw?.replace(/^http:\/\//, "https://");

/**
 * Optimize external image URLs for smaller display sizes.
 * Ticketmaster _SOURCE images are full-res (~1367x1365); replace with smaller variants.
 */
const optimizeImageUrl = (url?: string): string | undefined => {
  if (!url) return url;
  // Ticketmaster: upgrade _SOURCE (full-res) to retina portrait variant.
  // Note: do NOT rewrite other variant suffixes — many events don't have a
  // portrait 16:9 asset, which causes 404s / broken images.
  if (url.includes('ticketm.net/dam/') && url.endsWith('_SOURCE')) {
    return url.replace(/_SOURCE$/, '_RETINA_PORTRAIT_16_9.jpg');
  }
  return url;
};

export const ImageWithSkeleton: React.FC<ImageWithSkeletonProps> = ({
  src,
  alt,
  containerClassName,
  imgClassName,
  className,
  onLoad,
  onError,
  startVisible,
  fetchPriority,
  eagerLoad,
  ...rest
}) => {
  const initialRawSrc = typeof src === "string" ? src : undefined;
  const initialSrc = optimizeImageUrl(normalizeSrc(initialRawSrc));

  // Initialize from props to avoid a 1-frame “blank” render before useEffect runs.
  const [imgSrc, setImgSrc] = useState<string | undefined>(initialSrc);
  const [hasValidSrc, setHasValidSrc] = useState<boolean>(
    !!initialSrc && initialSrc !== "/placeholder.svg"
  );
  const [loaded, setLoaded] = useState<boolean>(!!startVisible);

  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const rawSrc = typeof src === "string" ? src : undefined;
    const normalized = optimizeImageUrl(normalizeSrc(rawSrc));

    setImgSrc(normalized);
    setHasValidSrc(!!normalized && normalized !== "/placeholder.svg");
    setLoaded(!!startVisible);
  }, [src, startVisible]);

  useLayoutEffect(() => {
    // If the image is already cached, avoid a one-frame skeleton flash.
    const img = imgRef.current;
    if (!img) return;
    if (hasValidSrc && img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [imgSrc, hasValidSrc]);

  return (
    <div className={cn("relative", containerClassName)}>
      {hasValidSrc && (
        <>
          <img
            ref={imgRef}
            src={imgSrc}
            alt={alt}
            className={cn(
              "w-full h-full object-cover transition-opacity duration-300",
              loaded ? "opacity-100" : "opacity-0",
              imgClassName || className
            )}
            loading={eagerLoad ? "eager" : "lazy"}
            decoding={eagerLoad ? "sync" : "async"}
            {...(fetchPriority ? { fetchPriority } : {})}
            referrerPolicy="no-referrer"
            onLoad={(e) => {
              setLoaded(true);
              onLoad?.(e);
            }}
            onError={(e) => {
              setHasValidSrc(false);
              onError?.(e);
            }}
            {...rest}
          />
          <div
            aria-hidden="true"
            className={cn(
              "absolute inset-0 bg-muted/20 transition-opacity duration-300",
              loaded ? "opacity-0" : "opacity-100"
            )}
          />
        </>
      )}
    </div>
  );
};

export default ImageWithSkeleton;

